import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * La card plegable de la ficha de un producto: ícono en un chip, título,
 * resumen cuando está cerrada y aviso en ámbar cuando le falta algo obligatorio.
 *
 * Vivía adentro de `FichaDeProducto.tsx`. Se extrajo cuando el editor de un post
 * del calendario de redes necesitó exactamente la misma card
 * (ADR-2026-09-15-el-post-se-edita-al-lado-del-calendario): copiarla era la
 * segunda versión de lo mismo, o sea el punto donde las dos empiezan a
 * divergir — el mismo motivo por el que se extrajo `ModalConfirmar`.
 */
export function Seccion({
  titulo,
  icono,
  resumen,
  aviso,
  plegable = true,
  abiertaPorDefecto,
  children,
}: {
  titulo: string;
  icono: React.ReactNode;
  resumen?: string;
  /** Texto en ámbar: algo obligatorio de esta sección todavía falta. */
  aviso?: string | null;
  plegable?: boolean;
  abiertaPorDefecto?: boolean;
  children: React.ReactNode;
}) {
  const [abierta, setAbierta] = useState(abiertaPorDefecto ?? !plegable);
  const contenido = <div className="space-y-3 px-4 pb-4 sm:px-5 sm:pb-5">{children}</div>;

  const cabecera = (
    <>
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-neutral-100 text-neutral-500">
        {icono}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-brand-black">{titulo}</span>
        {(aviso || (!abierta && resumen)) && (
          <span
            className={`mt-0.5 block truncate text-xs ${aviso ? 'text-amber-700' : 'text-neutral-400'}`}
          >
            {aviso ?? resumen}
          </span>
        )}
      </span>
    </>
  );

  return (
    <section className="overflow-hidden rounded-2xl border border-neutral-200 bg-surface-card shadow-sm">
      {plegable ? (
        <button
          type="button"
          onClick={() => setAbierta((v) => !v)}
          aria-expanded={abierta}
          className="flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-neutral-50/60 sm:p-5"
        >
          {cabecera}
          <ChevronDown
            className={`w-4 h-4 shrink-0 text-neutral-400 transition-transform ${abierta ? 'rotate-180' : ''}`}
          />
        </button>
      ) : (
        <div className="flex items-center gap-3 p-4 sm:p-5">{cabecera}</div>
      )}
      {abierta && contenido}
    </section>
  );
}

export default Seccion;
